import React, { useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import EffortRow from './EffortRow';
import { calculateProblemTotal } from '../utils/calculations';

const ProblemCard = ({
  problem,
  settings,
  updateProblem,
  removeProblem
}) => {
  const nameInputRef = useRef(null);
  const { cost } = calculateProblemTotal(problem, settings);

  useEffect(() => {
    if (!problem.name) {
      nameInputRef.current?.focus();
    }
  }, [problem.name]);

  const addEffort = () => {
    const newEffort = {
      id: `${Date.now()}${Math.random().toString(36).slice(2, 9)}`,
      name: '',
      freq: '',
      freqUnit: 'week',
      time: '',
      timeUnit: 'min'
    };

    updateProblem(problem.id, {
      efforts: [...problem.efforts, newEffort]
    });
  };

  const updateEffort = (effortId, field, value) => {
    const updatedEfforts = problem.efforts.map((effort) =>
      effort.id === effortId ? { ...effort, [field]: value } : effort
    );

    updateProblem(problem.id, {
      efforts: updatedEfforts,
      focusEffortId:
        problem.focusEffortId === effortId ? null : problem.focusEffortId
    });
  };

  const removeEffort = (effortId) => {
    updateProblem(problem.id, {
      efforts: problem.efforts.filter((effort) => effort.id !== effortId),
      focusEffortId: null
    });
  };

  const handleRemove = () => {
    if (window.confirm('Soll diese Problemstellung wirklich gelöscht werden?')) {
      removeProblem(problem.id);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-all hover:shadow-md print:shadow-none print:border-gray-400 print:break-inside-avoid">
      <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex justify-between items-center print:bg-white print:border-b-2 print:border-gray-800">
        <div className="flex items-center gap-3">
          <label
            htmlFor={`problem-name-${problem.id}`}
            className="text-xs font-bold uppercase tracking-wide text-gray-500"
          >
            Aufwand:
          </label>
          <input
            ref={nameInputRef}
            id={`problem-name-${problem.id}`}
            type="text"
            value={problem.name}
            onChange={(event) =>
              updateProblem(problem.id, { name: event.target.value })
            }
            placeholder="Namen eingeben"
            className="min-w-0 w-48 bg-transparent border-b border-gray-300 focus:border-red-500 focus:ring-0 px-1 py-1 font-semibold text-gray-700"
            aria-label="Name des Aufwands"
          />
        </div>

        <button
          onClick={handleRemove}
          className="text-gray-400 hover:text-red-500 flex items-center gap-1 text-xs font-medium no-print transition-colors"
        >
          <SafeIcon name="Trash2" />
          Entfernen
        </button>
      </div>

      <div className="p-6 bg-gray-50 print:bg-white">
        <h4 className="font-semibold text-gray-700 text-sm mb-4">
          Aufwand & Arbeitszeitverlust
        </h4>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
          {problem.efforts.length === 0 ? (
            <div className="py-8 text-center">
              <button
                onClick={addEffort}
                className="inline-flex items-center gap-2 text-sm text-red-600 font-semibold hover:underline"
              >
                <SafeIcon name="PlusCircle" />
                Aufwand hinzufügen
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {problem.efforts.map((effort, index) => (
                <EffortRow
                  key={effort.id}
                  effort={effort}
                  settings={settings}
                  updateEffort={updateEffort}
                  removeEffort={removeEffort}
                  autoFocus={
                    problem.focusEffortId === effort.id && index === 0
                  }
                />
              ))}
            </div>
          )}
        </div>

        {problem.efforts.length > 0 && (
          <button
            onClick={addEffort}
            className="mt-4 flex items-center gap-2 text-xs text-gray-500 hover:text-red-600 font-medium transition-colors no-print"
          >
            <SafeIcon name="PlusCircle" />
            Weiteren Aufwandsschritt hinzufügen
          </button>
        )}
      </div>

      <div className="bg-gray-800 px-6 py-4 flex justify-between items-center text-white print:bg-gray-100 print:text-black print:border-t">
        <div className="text-sm opacity-80">
          Jährlicher Verlust {problem.name.trim() || 'ohne Namen'}:
          <span className="font-mono ml-2 font-bold text-white print:text-black">
            {cost.toLocaleString('de-DE', {
              style: 'currency',
              currency: 'EUR',
              maximumFractionDigits: 0
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProblemCard;